import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchWithToken } from '../../services/fetchWithToken';
import './AddressMap.css';

// Font Awesome for reliable icon rendering
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot } from '@fortawesome/free-solid-svg-icons';

const AddressMap = () => {
  const mapRef = useRef(null);
  const [mapsKey, setMapsKey] = useState(null);
  const { t } = useTranslation();

  // Enhanced UTF-8 handling - bulletproof implementation
  useEffect(() => {
    // 1. Ensure charset meta exists and is first in head
    let charsetMeta = document.querySelector('meta[charset]');
    if (!charsetMeta) {
      charsetMeta = document.createElement('meta');
      charsetMeta.setAttribute('charset', 'utf-8');
      if (document.head.firstChild) {
        document.head.insertBefore(charsetMeta, document.head.firstChild);
      } else {
        document.head.appendChild(charsetMeta);
      }
    } else {
      // Ensure it's set to UTF-8
      charsetMeta.setAttribute('charset', 'utf-8');
      // Move to first position if it's not already there
      if (document.head.firstChild !== charsetMeta) {
        document.head.insertBefore(charsetMeta, document.head.firstChild);
      }
    }
    
    // 2. Ensure Content-Type meta with charset is present
    let contentTypeMeta = document.querySelector('meta[http-equiv="Content-Type"]');
    if (!contentTypeMeta) {
      contentTypeMeta = document.createElement('meta');
      contentTypeMeta.setAttribute('http-equiv', 'Content-Type');
      contentTypeMeta.setAttribute('content', 'text/html; charset=UTF-8');
      document.head.appendChild(contentTypeMeta);
    } else {
      contentTypeMeta.setAttribute('content', 'text/html; charset=UTF-8');
    }

    // 3. Add HTML5 lang attribute if not present
    if (!document.documentElement.hasAttribute('lang')) {
      document.documentElement.setAttribute('lang', 'en');
    }
    
    // 4. Add emoji and special character support with appropriate fonts
    const fontFamilies = [
      'Noto Color Emoji', // Google's emoji font
      'Segoe UI Emoji',   // Windows emoji font
      'Apple Color Emoji', // macOS/iOS emoji font
      'Twemoji Mozilla'   // Firefox emoji font
    ];
    
    fontFamilies.forEach(fontFamily => {
      let fontStyle = document.createElement('style');
      fontStyle.textContent = `
        @font-face {
          font-family: '${fontFamily}Fallback';
          src: local('${fontFamily}');
          unicode-range: U+1F300-1F5FF, U+1F600-1F64F, U+1F680-1F6FF, U+2600-26FF;
        }
        
        .emoji, .special-char {
          font-family: 
            '${fontFamily}', 
            'Noto Sans',
            'Helvetica', 
            'Arial', 
            sans-serif;
        }
      `;
      document.head.appendChild(fontStyle);
    });
    
    // 5. Set UTF-8 in response headers via script (best-effort)
    const script = document.createElement('script');
    script.textContent = `
      // Force text decoder to use UTF-8
      try {
        const originalFetch = window.fetch;
        window.fetch = function(input, init) {
          const promise = originalFetch(input, init);
          return promise.then(response => {
            // Force text decoder to use UTF-8
            const originalText = response.text;
            response.text = function() {
              return originalText.call(this).then(text => {
                try {
                  // Re-encode using UTF-8
                  const decoder = new TextDecoder('utf-8', {fatal: false});
                  const encoder = new TextEncoder();
                  return decoder.decode(encoder.encode(text));
                } catch(e) {
                  return text;
                }
              });
            };
            return response;
          });
        };
      } catch(e) {
        console.warn('UTF-8 fetch wrapper could not be applied', e);
      }
    `;
    document.head.appendChild(script);
  }, []);

  // 1) Fetch your Google Maps key from backend
  useEffect(() => {
    fetchWithToken('/api/config/maps')
      .then(res => res.json())
      .then(data => setMapsKey(data.apiKey))
      .catch(err => console.error('Error fetching Maps API key:', err));
  }, []);

  // 2) Once we have the key, load the Maps JS and draw
  useEffect(() => {
    if (!mapsKey) return;

    // inject script tag
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${mapsKey}&v=weekly`;
    script.async = true;
    script.defer = true;
    script.onload = initMap;
    document.head.appendChild(script);

    function initMap() {
      if (!window.google || !mapRef.current) return;

      // YOUR exact studio coords:
      const studio = { lat: 48.650703, lng: 20.527157 };

      const map = new window.google.maps.Map(mapRef.current, {
        center:           studio,
        zoom:             16,
        gestureHandling:  'greedy',      // allow dragging on mobile
        mapTypeControl:   false,
        fullscreenControl:false,
        streetViewControl:false
      });

      new window.google.maps.Marker({
        position: studio,
        map,
        title: t('address_map.studio_name'),
      });
    }

    // cleanup on unmount
    return () => {
      document.querySelectorAll(
        `script[src^="https://maps.googleapis.com/maps/api/js"]`
      ).forEach(s => s.remove());
    };
  }, [mapsKey, t]);

  return (
    <div className="address-card">
      <h3>{t('address_map.address')}</h3>
      <div className="map-container" ref={mapRef} style={{ height: '300px', borderRadius: '8px', overflow: 'hidden' }} />
      <div className="address-details">
        <p>Šafárikova 79, Rožňava</p>
        <p>{t('address_map.first_floor')}</p>
        <a
          href="https://www.google.com/maps/dir/?api=1&destination=48.650703,20.527157"
          target="_blank"
          rel="noopener noreferrer"
          className="get-directions"
        >
          <FontAwesomeIcon icon={faLocationDot} className="map-icon" />
          {t('address_map.get_directions')}
        </a>
      </div>
    </div>
  );
};

export default AddressMap; 